import type { Recount } from '../../game/recount';
import { t } from '../../i18n/es';
import { formatNumber } from '../format';

export interface RecountScreenOptions {
  recount: Recount;
  reducedMotion: boolean;
  onContinue: () => void;
}

/** Each line counts up in 200 ms, one after the other (GDD §13.2); 0 ms with reduced motion. */
export const LINE_MS = 200;

/**
 * RECUENTO (GDD §13.5, issue #62): the stamp, the lines of the recount with their count and points, the
 * level and run totals and the "NUEVA MARCA" badge. The points of each line count up with
 * requestAnimationFrame and measured time, so a controlled clock drives them; every line carries
 * data-state waiting, counting or done. Native elements until the design system lands (#15).
 */
export function recuentoScreen({ recount, reducedMotion, onContinue }: RecountScreenOptions): HTMLElement {
  const section = document.createElement('section');
  section.className = 'panel recount';
  section.setAttribute('aria-labelledby', 'recount-stamp');
  section.innerHTML = `
    <p class="tactical">${t('ui.screen.recuento')}</p>
    <h1 id="recount-stamp" class="display-mono" data-field="stamp">${recount.stamp}</h1>
    ${recount.newRecord ? `<p class="badge" data-field="record">${t('ui.recuento.newRecord')}</p>` : ''}
    ${recount.assisted ? `<p class="badge" data-field="assisted">${t('ui.recuento.assisted')}</p>` : ''}
    <div class="rule"></div>
    <ul class="recount-lines">${recount.lines
      .map(
        (line, index) => `
      <li data-line="${line.label}" data-index="${index}" data-final="${line.points}" data-state="waiting">
        <span class="tactical">${line.label}</span>
        <span class="readout" data-field="count">${line.count === null ? '' : formatNumber(line.count)}</span>
        <span class="readout" data-field="points">0</span>
      </li>`,
      )
      .join('')}</ul>
    <div class="rule"></div>
    <p class="recount-total"><span class="tactical">${t('ui.recuento.levelTotal')}</span>
      <span class="readout" data-field="level-total">${formatNumber(recount.levelTotal)}</span></p>
    <p class="recount-total"><span class="tactical">${t('ui.recuento.runTotal')}</span>
      <span class="readout" data-field="run-total">${formatNumber(recount.runTotal)}</span></p>
    <div class="slot-actions">
      <button type="button" class="button" data-action="continue">${t('ui.recuento.continue')}</button>
    </div>`;

  const items = [...section.querySelectorAll<HTMLLIElement>('[data-line]')];
  const show = (elapsed: number) => {
    for (const item of items) {
      const index = Number(item.dataset.index);
      const final = Number(item.dataset.final);
      const progress = reducedMotion ? 1 : Math.min(1, Math.max(0, (elapsed - index * LINE_MS) / LINE_MS));
      const started = reducedMotion || elapsed >= index * LINE_MS;
      item.dataset.state = progress >= 1 ? 'done' : started ? 'counting' : 'waiting';
      item.querySelector('[data-field="points"]')!.textContent = formatNumber(Math.round(final * progress));
    }
  };

  const start = performance.now();
  show(0);
  if (!reducedMotion) {
    const frame = () => {
      if (!section.isConnected) return;
      const elapsed = performance.now() - start;
      show(elapsed);
      if (elapsed < items.length * LINE_MS) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
    // Frames land every ~16 ms; a timer at each line boundary shows the exact end of every line on time.
    items.forEach((_, index) =>
      setTimeout(() => {
        if (section.isConnected) show(performance.now() - start);
      }, (index + 1) * LINE_MS),
    );
  }

  const button = section.querySelector<HTMLButtonElement>('[data-action="continue"]')!;
  button.addEventListener('click', onContinue);
  queueMicrotask(() => button.focus());
  return section;
}
